import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  TextField,
  Typography,
  useTheme,
  IconButton,
  Popover,
  Autocomplete,
  Button,
  Stack,
  Checkbox,
} from "@mui/material";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  flexRender,
} from "@tanstack/react-table";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FilterListOutlined } from "@mui/icons-material";
import { useGridSelection } from "./hooks/useGridSelection";
import {
  resolveDefaultColumnComponents,
  TextEditorCell,
} from "./components/defaultCellComponents";
import "./DataGrid.css";

export const SelectColumnId = "__tc_select__";

const defaultGetRowId = (row, index) => row?.id ?? index;

/**
 * Returns whether an event target is a text-editing element that should retain
 * native keyboard behavior (copy/paste, arrow keys, etc.).
 *
 * @param {EventTarget | null} target
 * @returns {boolean}
 */
const isTextInputTarget = (target) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName;
  return (
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    target.isContentEditable ||
    target.getAttribute("role") === "textbox"
  );
};

const canUseNavigatorClipboard = () => {
  return Boolean(window.isSecureContext && navigator.clipboard);
};

/**
 * Normalizes edit callback return values into a consistent object shape.
 * Supports primitive value returns, structured object returns, and null/undefined.
 *
 * @param {unknown} result
 * @param {object} currentRow
 * @param {unknown} currentValue
 * @returns {{ cancel: boolean, row: object, value: unknown }}
 */
const normalizeCallbackResult = (result, currentRow, currentValue) => {
  if (result == null) {
    return { cancel: false, row: currentRow, value: currentValue };
  }

  if (typeof result !== "object") {
    return { cancel: false, row: currentRow, value: result };
  }

  return {
    cancel: Boolean(result.cancel),
    row: result.row ?? currentRow,
    value: result.value ?? currentValue,
  };
};

const normalizeColumnOrder = ({
  columnOrder,
  stickyRightColumnId,
  selectColumn,
  selectColumnId,
}) => {
  let nextOrder = [...columnOrder];

  if (selectColumn && nextOrder.includes(selectColumnId)) {
    nextOrder = [
      selectColumnId,
      ...nextOrder.filter((columnId) => columnId !== selectColumnId),
    ];
  }

  if (stickyRightColumnId && nextOrder.includes(stickyRightColumnId)) {
    const withoutSticky = nextOrder.filter(
      (columnId) => columnId !== stickyRightColumnId
    );
    nextOrder = [...withoutSticky, stickyRightColumnId];
  }

  return nextOrder;
};

const areColumnOrdersEqual = (leftOrder, rightOrder) => {
  if (leftOrder === rightOrder) {
    return true;
  }

  if (!Array.isArray(leftOrder) || !Array.isArray(rightOrder)) {
    return false;
  }

  if (leftOrder.length !== rightOrder.length) {
    return false;
  }

  for (let index = 0; index < leftOrder.length; index += 1) {
    if (leftOrder[index] !== rightOrder[index]) {
      return false;
    }
  }

  return true;
};

const getUnderlyingColumnValue = (column, row) => {
  if (!column?.accessorKey) {
    return undefined;
  }
  return row?.[column.accessorKey];
};

/**
 * Resolves a cell value used for display/edit defaults.
 * Priority: `valueGetter` -> underlying `accessorKey` value.
 *
 * @param {object} column
 * @param {object} row
 * @param {number} rowIndex
 * @returns {unknown}
 */
const getComputedColumnValue = (column, row, rowIndex) => {
  if (typeof column?.valueGetter === "function") {
    return column.valueGetter({ row, rowIndex, column });
  }
  return getUnderlyingColumnValue(column, row);
};

/**
 * Resolves a value used for column filtering and type-ahead options.
 * Priority: `filterValueGetter` -> provided fallback -> computed value.
 *
 * @param {object} column
 * @param {object} row
 * @param {number} rowIndex
 * @param {unknown} fallbackValue
 * @returns {unknown}
 */
const getFilterColumnValue = (column, row, rowIndex, fallbackValue) => {
  if (typeof column?.filterValueGetter === "function") {
    return column.filterValueGetter({ row, rowIndex, column });
  }

  if (fallbackValue !== undefined) {
    return fallbackValue;
  }

  return getComputedColumnValue(column, row, rowIndex);
};

/**
 * Resolves a value used for sorting.
 * Priority: `sortValueGetter` -> provided fallback -> computed value.
 *
 * @param {object} column
 * @param {object} row
 * @param {number} rowIndex
 * @param {unknown} fallbackValue
 * @returns {unknown}
 */
const getSortColumnValue = (column, row, rowIndex, fallbackValue) => {
  if (typeof column?.sortValueGetter === "function") {
    return column.sortValueGetter({ row, rowIndex, column });
  }

  if (fallbackValue !== undefined) {
    return fallbackValue;
  }

  return getComputedColumnValue(column, row, rowIndex);
};

const compareSortableValues = (leftValue, rightValue) => {
  if (leftValue == null && rightValue == null) {
    return 0;
  }
  if (leftValue == null) {
    return -1;
  }
  if (rightValue == null) {
    return 1;
  }

  if (typeof leftValue === "number" && typeof rightValue === "number") {
    return leftValue - rightValue;
  }

  return String(leftValue).localeCompare(String(rightValue), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

const shouldShowColumnControls = (column) =>
  column?.showColumnControls !== false;

const getFilterPreviewText = (value) => {
  if (value == null) {
    return "";
  }
  const text = String(value).trim();
  return text;
};

const getEditorValueFromBlurTarget = (target) => {
  if (target instanceof HTMLInputElement) {
    if (target.type === "checkbox") {
      return Boolean(target.checked);
    }
    return target.value;
  }

  if (target instanceof HTMLTextAreaElement) {
    return target.value;
  }

  return undefined;
};

const buildCellClassName = ({
  isActive,
  inRange,
  inFillPreviewRange,
  userCellClassName,
}) => {
  let className = "tc-data-grid-cell";

  if (isActive) {
    className += " tc-data-grid-cell-selected";
  }

  if (inRange) {
    className += " tc-data-grid-cell-in-range";
  }

  if (inFillPreviewRange) {
    className += " tc-data-grid-cell-fill-preview";
  }

  if (userCellClassName) {
    className += ` ${userCellClassName}`;
  }

  return className;
};

/**
 * Draggable and resizable header cell renderer.
 * Keeps drag, resize, and column-controls concerns isolated from core grid logic.
 */
const SortableHeaderCell = ({
  columnId,
  headerStyles,
  borderColor,
  onResizeStart,
  onAutoSizeColumn,
  onOpenColumnControls,
  isSorted,
  isFiltered,
  filterPreviewText,
  isDraggable,
  showColumnControls,
  showResizer,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: columnId,
    disabled: !isDraggable,
  });

  return (
    <th
      ref={setNodeRef}
      className='tc-data-grid-header-cell'
      style={{
        ...headerStyles,
        borderColor,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.8 : 1,
      }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            minWidth: 0,
            overflow: "hidden",
            cursor: isDraggable ? "move" : "default",
          }}
          {...(isDraggable ? attributes : {})}
          {...(isDraggable ? listeners : {})}>
          <Typography
            variant='body2'
            sx={{
              fontSize: "1rem",
              fontWeight: 700,
              textOverflow: "ellipsis",
              overflow: "hidden",
            }}>
            {children}
          </Typography>
        </Box>
        {showColumnControls ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              minWidth: 0,
              maxWidth: 140,
            }}>
            {filterPreviewText ? (
              <Typography
                variant='caption'
                sx={{
                  fontSize: "0.65rem",
                  opacity: 0.95,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 96,
                }}
                title={filterPreviewText}>
                {filterPreviewText}
              </Typography>
            ) : null}
            <IconButton
              size='small'
              aria-label={`Column controls for ${children}`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onOpenColumnControls(columnId, event.currentTarget);
              }}
              sx={{
                color: "inherit",
                opacity: isSorted || isFiltered ? 1 : 0.75,
                p: 0.25,
              }}>
              <FilterListOutlined fontSize='small' />
            </IconButton>
          </Box>
        ) : null}
        {showResizer ? (
          <span
            className='tc-data-grid-resizer'
            onMouseDown={(event) => onResizeStart(event, columnId)}
            onDoubleClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onAutoSizeColumn(columnId);
            }}
          />
        ) : null}
      </Box>
    </th>
  );
};

/**
 * Reusable editable DataGrid with spreadsheet-like interaction model.
 *
 * Extensibility points:
 * - Column-level render/edit/value strategies (`displayComponent`, `editComponent`, `valueGetter`, etc.)
 * - Row/cell styling hooks (`getRowClassName`, `getCellStyle`, etc.)
 * - Edit interception pipeline (`onCellEdit` then `onRowEdit`)
 * - Selection integration (`selectedRows`, `onSelectedRowsChange`)
 *
 * @param {object} props
 * @param {Array<object>} props.rows
 * @param {Array<object>} props.columns
 * @param {(nextRows: Array<object>, metadata?: object) => void} [props.onRowsChange]
 * @param {(change: object) => (unknown|{value?: unknown, row?: object, cancel?: boolean}|Promise<unknown|{value?: unknown, row?: object, cancel?: boolean}>)} [props.onCellEdit]
 * @param {(change: object) => (unknown|{value?: unknown, row?: object, cancel?: boolean}|Promise<unknown|{value?: unknown, row?: object, cancel?: boolean}>)} [props.onRowEdit]
 * @param {(context: object) => string} [props.getRowClassName]
 * @param {(context: object) => object} [props.getRowStyle]
 * @param {(context: object) => string} [props.getCellClassName]
 * @param {(context: object) => object} [props.getCellStyle]
 * @param {boolean} [props.selectColumn=false]
 * @param {Set<unknown>} [props.selectedRows]
 * @param {(nextSelectedRows: Set<unknown>) => void} [props.onSelectedRowsChange]
 * @param {(visibleRowIds: Array<string|number>) => void} [props.onVisibleRowIdsChange]
 * @param {(row: object, index: number) => string|number} [props.getRowId]
 * @param {boolean} [props.searchable=true]
 * @param {boolean} [props.sortable=true]
 * @param {boolean} [props.draggableColumns=true]
 * @param {boolean} [props.resizableColumns=true]
 * @param {boolean} [props.selectableRange=true]
 * @param {boolean} [props.enableClipboard=true]
 * @param {boolean} [props.enableFillHandle=true]
 * @param {boolean} [props.alternateRowColors=false]
 * @param {string} [props.stickyRightColumnId]
 * @param {Array<string>} [props.initialColumnOrder]
 * @param {(nextColumnOrder: Array<string>) => void} [props.onColumnOrderChange]
 * @param {string|number} [props.height='100%']
 * @param {string} [props.searchPlaceholder='Search']
 */
export const DataGrid = ({
  rows,
  columns,
  onRowsChange,
  onCellEdit,
  onRowEdit,
  getRowClassName,
  getRowStyle,
  getCellClassName,
  getCellStyle,
  selectColumn = false,
  selectedRows,
  onSelectedRowsChange,
  onVisibleRowIdsChange,
  getRowId = defaultGetRowId,
  searchable = true,
  sortable = true,
  draggableColumns = true,
  resizableColumns = true,
  selectableRange = true,
  enableClipboard = true,
  enableFillHandle = true,
  alternateRowColors = false,
  stickyRightColumnId,
  initialColumnOrder,
  onColumnOrderChange,
  height = "100%",
  searchPlaceholder = "Search",
}) => {
  const theme = useTheme();
  const [internalRows, setInternalRows] = useState(rows ?? []);
  const [internalSelectedRows, setInternalSelectedRows] = useState(
    () => new Set()
  );
  const selectedRowsSet = selectedRows ?? internalSelectedRows;

  const selectableColumns = useMemo(() => {
    if (!selectColumn) {
      return columns;
    }

    return [
      {
        id: SelectColumnId,
        accessorKey: SelectColumnId,
        header: "",
        width: 48,
        minWidth: 48,
        maxWidth: 48,
        editable: false,
        searchable: false,
        sortable: false,
        resizable: false,
        draggable: false,
        disableAutoWidth: true,
      },
      ...columns,
    ];
  }, [columns, selectColumn]);

  const [columnOrder, setColumnOrder] = useState(() =>
    normalizeColumnOrder({
      columnOrder: (() => {
        const availableColumnIds = selectableColumns.map(
          (column) => column.id || column.accessorKey
        );
        const preferredOrder =
          Array.isArray(initialColumnOrder) && initialColumnOrder.length > 0
            ? initialColumnOrder
            : availableColumnIds;
        const filtered = preferredOrder.filter((columnId) =>
          availableColumnIds.includes(columnId)
        );
        const additions = availableColumnIds.filter(
          (columnId) => !filtered.includes(columnId)
        );
        return [...filtered, ...additions];
      })(),
      stickyRightColumnId,
      selectColumn,
      selectColumnId: SelectColumnId,
    })
  );
  const [columnSizes, setColumnSizes] = useState(() => {
    const widths = {};
    selectableColumns.forEach((column) => {
      const id = column.id || column.accessorKey;
      widths[id] = column.width || 160;
    });
    return widths;
  });
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  const [rowHeight, setRowHeight] = useState(34);
  const [autoDistributeWidths, setAutoDistributeWidths] = useState(true);
  const [editingCell, setEditingCell] = useState(null);
  const [fillDragEndCell, setFillDragEndCell] = useState(null);
  const [isFillDragging, setIsFillDragging] = useState(false);
  const [isSelectingRange, setIsSelectingRange] = useState(false);
  const [columnControlState, setColumnControlState] = useState({
    open: false,
    anchorEl: null,
    columnId: null,
    columnName: "",
    filterValue: "",
  });

  const resizingRef = useRef(null);
  const resizeAnimationFrameRef = useRef(null);
  const pendingResizeRef = useRef(null);
  const scrollerRef = useRef(null);
  const selectedRowsSetRef = useRef(selectedRowsSet);
  const typeAheadCacheRef = useRef(new Map());

  const { activeCell, range, setSingleCell, setRangeEnd, isCellInRange } =
    useGridSelection();

  useEffect(() => {
    setInternalRows(rows ?? []);
  }, [rows]);

  useEffect(() => {
    const nextOrder = selectableColumns.map(
      (column) => column.id || column.accessorKey
    );
    setColumnOrder((existing) => {
      const preferredOrder =
        Array.isArray(initialColumnOrder) && initialColumnOrder.length > 0
          ? initialColumnOrder
          : existing;
      const filtered = preferredOrder.filter((id) => nextOrder.includes(id));
      const additions = nextOrder.filter((id) => !filtered.includes(id));
      const normalizedOrder = normalizeColumnOrder({
        columnOrder: [...filtered, ...additions],
        stickyRightColumnId,
        selectColumn,
        selectColumnId: SelectColumnId,
      });

      return areColumnOrdersEqual(existing, normalizedOrder)
        ? existing
        : normalizedOrder;
    });
    setColumnSizes((existing) => {
      const nextSizes = {};
      selectableColumns.forEach((column) => {
        const id = column.id || column.accessorKey;
        nextSizes[id] = existing[id] || column.width || 160;
      });
      return nextSizes;
    });
    setAutoDistributeWidths(true);
  }, [
    initialColumnOrder,
    selectColumn,
    selectableColumns,
    stickyRightColumnId,
  ]);

  useEffect(() => {
    if (!onColumnOrderChange) {
      return;
    }

    onColumnOrderChange(columnOrder);
  }, [columnOrder, onColumnOrderChange]);

  useEffect(() => {
    if (selectedRows instanceof Set) {
      setInternalSelectedRows(new Set(selectedRows));
    }
  }, [selectedRows]);

  useEffect(() => {
    selectedRowsSetRef.current = selectedRowsSet;
  }, [selectedRowsSet]);

  useEffect(() => {
    typeAheadCacheRef.current.clear();
  }, [internalRows]);

  const applyRows = useCallback(
    (nextRows, changeMeta) => {
      setInternalRows(nextRows);
      if (onRowsChange) {
        onRowsChange(nextRows, changeMeta);
      }
    },
    [onRowsChange]
  );

  const normalizedColumns = useMemo(() => {
    return selectableColumns.map((column) =>
      resolveDefaultColumnComponents(column)
    );
  }, [selectableColumns]);

  /**
   * Applies selection updates to internal state and external callback in one place
   * to keep row-selection behavior consistent across header and row checkboxes.
   */
  const applySelectedRowsChange = useCallback(
    (nextSelectedRows) => {
      setInternalSelectedRows(nextSelectedRows);
      if (onSelectedRowsChange) {
        onSelectedRowsChange(nextSelectedRows);
      }
    },
    [onSelectedRowsChange]
  );

  const toggleRowSelection = useCallback(
    (rowId, checked) => {
      const nextSelectedRows = new Set(selectedRowsSetRef.current);
      if (checked) {
        nextSelectedRows.add(rowId);
      } else {
        nextSelectedRows.delete(rowId);
      }

      applySelectedRowsChange(nextSelectedRows);
    },
    [applySelectedRowsChange]
  );

  const columnLookup = useMemo(() => {
    return new Map(
      normalizedColumns.map((column) => [
        column.id || column.accessorKey,
        column,
      ])
    );
  }, [normalizedColumns]);

  const orderedColumns = useMemo(() => {
    return columnOrder
      .map((columnId) => columnLookup.get(columnId))
      .filter(Boolean)
      .map((column) => ({
        ...column,
        id: column.id || column.accessorKey,
        size:
          columnSizes[column.id || column.accessorKey] || column.width || 160,
      }));
  }, [columnLookup, columnOrder, columnSizes]);

  const tableColumns = useMemo(() => {
    return orderedColumns.map((column) => {
      const sortingEnabled = sortable && column.sortable !== false;
      const filterEnabled = searchable && column.searchable !== false;

      return {
        id: column.id,
        accessorKey: column.accessorKey,
        accessorFn: (row, index) => getComputedColumnValue(column, row, index),
        size: column.size,
        minSize: column.minWidth || 100,
        maxSize: column.maxWidth,
        header: column.header,
        meta: {
          showColumnControls: shouldShowColumnControls(column),
        },
        cell: (context) => {
          if (column.id === SelectColumnId) {
            const rowId = getRowId(context.row.original, context.row.index);
            const isChecked = selectedRowsSetRef.current.has(rowId);

            return (
              <Checkbox
                size='small'
                checked={isChecked}
                inputProps={{ "aria-label": `Select row ${rowId}` }}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                onChange={(event) => {
                  toggleRowSelection(rowId, event.target.checked);
                }}
              />
            );
          }

          const cellValue = context.getValue();
          if (column.displayComponent) {
            return column.displayComponent({
              value: cellValue,
              row: context.row.original,
              rowIndex: context.row.index,
              column,
            });
          }
          if (column.formatValue) {
            return column.formatValue(cellValue, context.row.original);
          }
          return cellValue == null ? "" : String(cellValue);
        },
        enableSorting: sortingEnabled,
        enableColumnFilter: filterEnabled,
        sortingFn: (rowA, rowB, columnId) => {
          const leftFallbackValue = rowA.getValue(columnId);
          const rightFallbackValue = rowB.getValue(columnId);
          const leftValue = getSortColumnValue(
            column,
            rowA.original,
            rowA.index,
            leftFallbackValue
          );
          const rightValue = getSortColumnValue(
            column,
            rowB.original,
            rowB.index,
            rightFallbackValue
          );
          return compareSortableValues(leftValue, rightValue);
        },
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) {
            return true;
          }
          const fallbackValue = row.getValue(columnId);
          const value = getFilterColumnValue(
            column,
            row.original,
            row.index,
            fallbackValue
          );
          if (value == null) {
            return false;
          }
          return String(value)
            .toLowerCase()
            .includes(String(filterValue).toLowerCase());
        },
      };
    });
  }, [
    getRowId,
    orderedColumns,
    searchable,
    SelectColumnId,
    sortable,
    toggleRowSelection,
  ]);

  const table = useReactTable({
    data: internalRows,
    columns: tableColumns,
    getRowId: (originalRow, index) => String(getRowId(originalRow, index)),
    state: {
      sorting,
      columnFilters,
      columnOrder,
      columnSizing: columnSizes,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: (updater) => {
      setColumnOrder((existing) => {
        const nextOrder =
          typeof updater === "function" ? updater(existing) : updater;
        const normalizedOrder = normalizeColumnOrder({
          columnOrder: nextOrder,
          stickyRightColumnId,
          selectColumn,
          selectColumnId: SelectColumnId,
        });

        return areColumnOrdersEqual(existing, normalizedOrder)
          ? existing
          : normalizedOrder;
      });
    },
    onColumnSizingChange: setColumnSizes,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    columnResizeMode: "onChange",
  });

  const visibleRows = table.getRowModel().rows;
  const visibleLeafColumns = table.getVisibleLeafColumns();
  const rowVirtualizationOverscan = 8;

  const virtualRange = useMemo(() => {
    if (visibleRows.length === 0) {
      return { start: 0, end: -1 };
    }

    if (rowHeight <= 0 || viewportHeight <= 0) {
      return { start: 0, end: visibleRows.length - 1 };
    }

    const start = Math.max(
      0,
      Math.floor(scrollTop / rowHeight) - rowVirtualizationOverscan
    );
    const end = Math.min(
      visibleRows.length - 1,
      Math.ceil((scrollTop + viewportHeight) / rowHeight) +
        rowVirtualizationOverscan
    );

    return { start, end };
  }, [rowHeight, scrollTop, viewportHeight, visibleRows.length]);

  const renderedRows = useMemo(() => {
    if (virtualRange.end < virtualRange.start) {
      return [];
    }
    return visibleRows.slice(virtualRange.start, virtualRange.end + 1);
  }, [virtualRange.end, virtualRange.start, visibleRows]);

  const getBaseRowIndexFromVisibleRowIndex = useCallback(
    (visibleRowIndex) => {
      const mappedIndex = visibleRows[visibleRowIndex]?.index;
      return typeof mappedIndex === "number" ? mappedIndex : visibleRowIndex;
    },
    [visibleRows]
  );

  const topSpacerHeight =
    virtualRange.start > 0 ? virtualRange.start * rowHeight : 0;
  const bottomSpacerHeight =
    virtualRange.end >= 0 && virtualRange.end < visibleRows.length - 1
      ? (visibleRows.length - virtualRange.end - 1) * rowHeight
      : 0;

  const visibleSelectedRowIds = useMemo(() => {
    return visibleRows.map((row) => getRowId(row.original, row.index));
  }, [getRowId, visibleRows]);

  useEffect(() => {
    if (typeof onVisibleRowIdsChange === "function") {
      onVisibleRowIdsChange(visibleSelectedRowIds);
    }
  }, [onVisibleRowIdsChange, visibleSelectedRowIds]);

  const allVisibleRowsSelected = useMemo(() => {
    return (
      visibleSelectedRowIds.length > 0 &&
      visibleSelectedRowIds.every((rowId) => selectedRowsSet.has(rowId))
    );
  }, [selectedRowsSet, visibleSelectedRowIds]);

  const someVisibleRowsSelected = useMemo(() => {
    return visibleSelectedRowIds.some((rowId) => selectedRowsSet.has(rowId));
  }, [selectedRowsSet, visibleSelectedRowIds]);

  const toggleVisibleRowsSelection = useCallback(
    (checked) => {
      const nextSelectedRows = new Set(selectedRowsSet);

      if (checked) {
        visibleSelectedRowIds.forEach((rowId) => {
          nextSelectedRows.add(rowId);
        });
      } else {
        visibleSelectedRowIds.forEach((rowId) => {
          nextSelectedRows.delete(rowId);
        });
      }

      applySelectedRowsChange(nextSelectedRows);
    },
    [applySelectedRowsChange, selectedRowsSet, visibleSelectedRowIds]
  );

  const selectedRange = useMemo(() => {
    if (range) {
      return range;
    }
    if (activeCell) {
      return {
        start: activeCell,
        end: activeCell,
      };
    }
    return null;
  }, [activeCell, range]);

  const fillPreviewRange = useMemo(() => {
    if (!isFillDragging || !activeCell || !fillDragEndCell) {
      return null;
    }

    return {
      start: {
        rowIndex: Math.min(activeCell.rowIndex, fillDragEndCell.rowIndex),
        colIndex: Math.min(activeCell.colIndex, fillDragEndCell.colIndex),
      },
      end: {
        rowIndex: Math.max(activeCell.rowIndex, fillDragEndCell.rowIndex),
        colIndex: Math.max(activeCell.colIndex, fillDragEndCell.colIndex),
      },
    };
  }, [activeCell, fillDragEndCell, isFillDragging]);

  const isCellInFillPreviewRange = useCallback(
    (rowIndex, colIndex) => {
      if (!fillPreviewRange) {
        return false;
      }

      return (
        rowIndex >= fillPreviewRange.start.rowIndex &&
        rowIndex <= fillPreviewRange.end.rowIndex &&
        colIndex >= fillPreviewRange.start.colIndex &&
        colIndex <= fillPreviewRange.end.colIndex
      );
    },
    [fillPreviewRange]
  );

  const displayColumnWidths = useMemo(() => {
    const baseColumns = visibleLeafColumns.map((column) => {
      const configuredWidth = columnSizes[column.id];
      const fallbackWidth =
        column.getSize?.() || columnLookup.get(column.id)?.width || 160;
      return {
        id: column.id,
        width: configuredWidth || fallbackWidth,
      };
    });

    const baseTotalWidth = baseColumns.reduce(
      (sum, column) => sum + column.width,
      0
    );

    if (
      !autoDistributeWidths ||
      !availableWidth ||
      baseTotalWidth >= availableWidth ||
      baseColumns.length === 0
    ) {
      return new Map(baseColumns.map((column) => [column.id, column.width]));
    }

    const extraWidth = availableWidth - baseTotalWidth;
    const autoWidthColumns = baseColumns.filter((column) => {
      const columnDef = columnLookup.get(column.id);
      return !columnDef?.disableAutoWidth;
    });

    if (autoWidthColumns.length === 0) {
      return new Map(baseColumns.map((column) => [column.id, column.width]));
    }

    const extraPerColumn = extraWidth / autoWidthColumns.length;

    return new Map(
      baseColumns.map((column) => {
        const columnDef = columnLookup.get(column.id);
        if (columnDef?.disableAutoWidth) {
          return [column.id, column.width];
        }
        return [column.id, column.width + extraPerColumn];
      })
    );
  }, [
    autoDistributeWidths,
    availableWidth,
    columnLookup,
    columnSizes,
    visibleLeafColumns,
  ]);

  const applySingleEditToRows = useCallback(
    /**
     * Applies one edit request to a row array while running callback hooks.
     * Returns a new row array when mutation is needed, or the original array for no-op/cancel.
     */
    async (baseRows, { rowIndex, columnId, value }) => {
      const baseRowIndex = getBaseRowIndexFromVisibleRowIndex(rowIndex);
      const columnDef = columnLookup.get(columnId);
      const currentRow = baseRows[baseRowIndex];

      if (!currentRow || !columnDef) {
        return baseRows;
      }

      if (!columnDef.accessorKey) {
        return baseRows;
      }

      const oldValue = currentRow[columnDef.accessorKey];
      const parsedValue = columnDef.parseValue
        ? columnDef.parseValue(value, currentRow)
        : value;

      if (!onCellEdit && !onRowEdit && Object.is(oldValue, parsedValue)) {
        return baseRows;
      }

      const nextRows = [...baseRows];

      let updatedRow = {
        ...currentRow,
        [columnDef.accessorKey]: parsedValue,
      };
      let updatedValue = parsedValue;

      if (onCellEdit) {
        const result = normalizeCallbackResult(
          await onCellEdit({
            row: updatedRow,
            rowIndex: baseRowIndex,
            columnId,
            column: columnDef,
            oldValue,
            newValue: parsedValue,
            rows: nextRows,
          }),
          updatedRow,
          parsedValue
        );

        if (result.cancel) {
          return nextRows;
        }

        updatedRow = {
          ...result.row,
          [columnDef.accessorKey]: result.value,
        };
        updatedValue = result.value;
      }

      if (onRowEdit) {
        const rowResult = normalizeCallbackResult(
          await onRowEdit({
            row: updatedRow,
            rowIndex: baseRowIndex,
            columnId,
            column: columnDef,
            oldValue,
            newValue: updatedValue,
            rows: nextRows,
          }),
          updatedRow,
          updatedValue
        );

        if (rowResult.cancel) {
          return nextRows;
        }

        updatedRow = {
          ...rowResult.row,
          [columnDef.accessorKey]: rowResult.value,
        };
      }

      nextRows[baseRowIndex] = updatedRow;
      return nextRows;
    },
    [columnLookup, getBaseRowIndexFromVisibleRowIndex, onCellEdit, onRowEdit]
  );

  const applyMultipleEdits = useCallback(
    /**
     * Sequentially applies multiple edit operations so each edit sees the latest row state.
     * Emits `onRowsChange` once with consolidated metadata.
     */
    async (edits) => {
      if (!edits || edits.length === 0) {
        return;
      }

      let workingRows = internalRows;
      const appliedEdits = [];
      let anyRowReplaced = false;
      for (const edit of edits) {
        const { rowIndex, columnId, value: requestedValue } = edit;
        const baseRowIndex = getBaseRowIndexFromVisibleRowIndex(rowIndex);
        const previousRows = workingRows;
        const previousRow = previousRows[baseRowIndex];
        const previousRowId =
          previousRow == null ? undefined : getRowId(previousRow, baseRowIndex);
        const columnDef = columnLookup.get(columnId);
        const accessorKey = columnDef?.accessorKey;
        const oldValue =
          previousRow && accessorKey ? previousRow[accessorKey] : undefined;

        workingRows = await applySingleEditToRows(workingRows, edit);

        if (workingRows[baseRowIndex] !== previousRow) {
          anyRowReplaced = true;
        }

        const nextRow = workingRows[baseRowIndex];
        const nextValue =
          nextRow && accessorKey ? nextRow[accessorKey] : undefined;
        const changed = !Object.is(oldValue, nextValue);

        if (changed) {
          appliedEdits.push({
            rowIndex: baseRowIndex,
            rowId: previousRowId,
            columnId,
            accessorKey,
            oldValue,
            newValue: nextValue,
            requestedValue,
          });
        }
      }

      // Nothing actually changed — every edit was a no-op or was cancelled by onCellEdit/onRowEdit.
      // Bail rather than emitting onRowsChange, which consumers treat as "rows were edited" and which
      // would otherwise rebuild every row for an edit that was entirely rejected.
      if (appliedEdits.length === 0 && !anyRowReplaced) {
        return;
      }

      applyRows(workingRows, {
        source: "grid-edit",
        requestedEdits: edits,
        appliedEdits,
      });
    },
    [
      applyRows,
      applySingleEditToRows,
      columnLookup,
      getBaseRowIndexFromVisibleRowIndex,
      getRowId,
      internalRows,
    ]
  );

  const buildSelectionClipboardText = useCallback(() => {
    if (!enableClipboard || !selectedRange) {
      return "";
    }

    const rowLines = [];
    for (
      let rowIndex = selectedRange.start.rowIndex;
      rowIndex <= selectedRange.end.rowIndex;
      rowIndex += 1
    ) {
      const row = visibleRows[rowIndex]?.original;
      if (!row) {
        continue;
      }

      const cellValues = [];
      for (
        let colIndex = selectedRange.start.colIndex;
        colIndex <= selectedRange.end.colIndex;
        colIndex += 1
      ) {
        const columnId = visibleLeafColumns[colIndex]?.id;
        const columnDef = columnLookup.get(columnId);
        if (!columnDef) {
          cellValues.push("");
          continue;
        }
        const value = getComputedColumnValue(columnDef, row, rowIndex);
        cellValues.push(value == null ? "" : String(value));
      }
      rowLines.push(cellValues.join("\t"));
    }

    return rowLines.join("\n");
  }, [
    columnLookup,
    enableClipboard,
    selectedRange,
    visibleLeafColumns,
    visibleRows,
  ]);

  const applyPastedText = useCallback(
    /**
     * Parses tab/newline clipboard text and maps it to grid edit operations.
     * Supports single-value fill across an explicit selected range.
     */
    async (text) => {
      if (!text || !activeCell) {
        return;
      }

      const matrix = text
        .split(/\r?\n/)
        .filter((line) => line.length > 0)
        .map((line) => line.split("\t"));

      const hasExplicitRange = Boolean(
        range &&
        (range.start?.rowIndex !== range.end?.rowIndex ||
          range.start?.colIndex !== range.end?.colIndex)
      );
      const targetRange = hasExplicitRange ? selectedRange : null;
      const rangeRowCount = targetRange
        ? targetRange.end.rowIndex - targetRange.start.rowIndex + 1
        : 1;
      const rangeColCount = targetRange
        ? targetRange.end.colIndex - targetRange.start.colIndex + 1
        : 1;

      if (
        targetRange &&
        (rangeRowCount > 1 || rangeColCount > 1) &&
        matrix.length === 1 &&
        matrix[0].length === 1
      ) {
        const edits = [];
        for (
          let rowIndex = targetRange.start.rowIndex;
          rowIndex <= targetRange.end.rowIndex;
          rowIndex += 1
        ) {
          for (
            let colIndex = targetRange.start.colIndex;
            colIndex <= targetRange.end.colIndex;
            colIndex += 1
          ) {
            const columnId = visibleLeafColumns[colIndex]?.id;
            if (!columnId) {
              continue;
            }
            edits.push({ rowIndex, columnId, value: matrix[0][0] });
          }
        }
        await applyMultipleEdits(edits);
        return;
      }

      const startRow = targetRange
        ? targetRange.start.rowIndex
        : activeCell.rowIndex;
      const startCol = targetRange
        ? targetRange.start.colIndex
        : activeCell.colIndex;
      const maxRows = targetRange
        ? Math.min(matrix.length, rangeRowCount)
        : matrix.length;

      const edits = [];
      for (let rowOffset = 0; rowOffset < maxRows; rowOffset += 1) {
        const maxCols = targetRange
          ? Math.min(matrix[rowOffset].length, rangeColCount)
          : matrix[rowOffset].length;
        for (let colOffset = 0; colOffset < maxCols; colOffset += 1) {
          const targetRow = startRow + rowOffset;
          const targetCol = startCol + colOffset;
          const columnId = visibleLeafColumns[targetCol]?.id;
          if (!columnId) {
            continue;
          }
          edits.push({
            rowIndex: targetRow,
            columnId,
            value: matrix[rowOffset][colOffset],
          });
        }
      }

      await applyMultipleEdits(edits);
    },
    [activeCell, applyMultipleEdits, range, selectedRange, visibleLeafColumns]
  );

  const pasteFromClipboard = useCallback(async () => {
    if (!enableClipboard || !activeCell) {
      return;
    }

    if (!canUseNavigatorClipboard() || !navigator.clipboard?.readText) {
      return;
    }

    let text = "";
    try {
      text = await navigator.clipboard.readText();
    } catch {
      return;
    }

    if (!text) {
      return;
    }

    await applyPastedText(text);
  }, [activeCell, applyPastedText, enableClipboard]);

  useEffect(() => {
    const onMouseMove = (event) => {
      if (!resizingRef.current) {
        return;
      }

      const { columnId, startX, initialWidth, minWidth, maxWidth } =
        resizingRef.current;
      const delta = event.clientX - startX;
      const nextWidth = Math.max(minWidth, initialWidth + delta);
      const boundedWidth = maxWidth ? Math.min(maxWidth, nextWidth) : nextWidth;

      pendingResizeRef.current = { columnId, width: boundedWidth };

      if (resizeAnimationFrameRef.current != null) {
        return;
      }

      resizeAnimationFrameRef.current = window.requestAnimationFrame(() => {
        const pendingResize = pendingResizeRef.current;
        resizeAnimationFrameRef.current = null;
        if (!pendingResize) {
          return;
        }

        setColumnSizes((previous) => {
          if (previous[pendingResize.columnId] === pendingResize.width) {
            return previous;
          }

          return {
            ...previous,
            [pendingResize.columnId]: pendingResize.width,
          };
        });
        setAutoDistributeWidths(false);
      });
    };

    const onMouseUp = () => {
      if (resizeAnimationFrameRef.current != null) {
        window.cancelAnimationFrame(resizeAnimationFrameRef.current);
        resizeAnimationFrameRef.current = null;
      }

      const pendingResize = pendingResizeRef.current;
      pendingResizeRef.current = null;
      if (pendingResize) {
        setColumnSizes((previous) => {
          if (previous[pendingResize.columnId] === pendingResize.width) {
            return previous;
          }

          return {
            ...previous,
            [pendingResize.columnId]: pendingResize.width,
          };
        });
        setAutoDistributeWidths(false);
      }

      resizingRef.current = null;
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      if (resizeAnimationFrameRef.current != null) {
        window.cancelAnimationFrame(resizeAnimationFrameRef.current);
        resizeAnimationFrameRef.current = null;
      }

      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const startResize = useCallback(
    (event, columnId) => {
      if (!resizableColumns) {
        return;
      }
      event.preventDefault();
      const column = columnLookup.get(columnId);
      if (!column) {
        return;
      }

      resizingRef.current = {
        columnId,
        startX: event.clientX,
        initialWidth: columnSizes[columnId] || column.width || 160,
        minWidth: column.minWidth || 100,
        maxWidth: column.maxWidth,
      };
    },
    [columnLookup, columnSizes, resizableColumns]
  );

  const autoSizeColumn = useCallback(
    (columnId) => {
      if (!resizableColumns) {
        return;
      }

      const column = columnLookup.get(columnId);
      if (!column || column.resizable === false) {
        return;
      }

      const scrollerElement = scrollerRef.current;
      if (!scrollerElement) {
        return;
      }

      const columnIndex = visibleLeafColumns.findIndex(
        (visibleColumn) => visibleColumn.id === columnId
      );
      if (columnIndex < 0) {
        return;
      }

      const targetIndex = columnIndex + 1;
      const headerCell = scrollerElement.querySelector(
        `thead th:nth-child(${targetIndex})`
      );
      const bodyCells = Array.from(
        scrollerElement.querySelectorAll(
          `tbody tr td:nth-child(${targetIndex})`
        )
      );
      const measurementTargets = [headerCell, ...bodyCells].filter(Boolean);

      if (measurementTargets.length === 0) {
        return;
      }

      const originalWidths = measurementTargets.map(
        (element) => element.style.width
      );
      let measuredContentWidth = 0;

      try {
        measurementTargets.forEach((element) => {
          element.style.width = "auto";
        });

        measuredContentWidth = measurementTargets.reduce(
          (maxWidth, element) => {
            return Math.max(maxWidth, element.scrollWidth || 0);
          },
          0
        );
      } finally {
        measurementTargets.forEach((element, index) => {
          element.style.width = originalWidths[index];
        });
      }

      if (measuredContentWidth <= 0) {
        return;
      }

      const widthWithPadding = measuredContentWidth + 2;
      const minWidth = column.minWidth || 100;
      const boundedWidth = column.maxWidth
        ? Math.min(column.maxWidth, Math.max(minWidth, widthWithPadding))
        : Math.max(minWidth, widthWithPadding);

      setColumnSizes((previous) => ({
        ...previous,
        [columnId]: boundedWidth,
      }));
      setAutoDistributeWidths(false);
    },
    [columnLookup, resizableColumns, visibleLeafColumns]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const onDragEnd = useCallback(
    ({ active, over }) => {
      if (!draggableColumns || !over || active.id === over.id) {
        return;
      }
      if (
        selectColumn &&
        (active.id === SelectColumnId || over.id === SelectColumnId)
      ) {
        return;
      }
      if (
        stickyRightColumnId &&
        (active.id === stickyRightColumnId || over.id === stickyRightColumnId)
      ) {
        return;
      }
      setColumnOrder((previous) => {
        const oldIndex = previous.indexOf(active.id);
        const newIndex = previous.indexOf(over.id);
        return normalizeColumnOrder({
          columnOrder: arrayMove(previous, oldIndex, newIndex),
          stickyRightColumnId,
          selectColumn,
          selectColumnId: SelectColumnId,
        });
      });
    },
    [draggableColumns, selectColumn, SelectColumnId, stickyRightColumnId]
  );

  const onCellMouseDown = useCallback(
    (rowIndex, colIndex, shiftKey) => {
      if (!selectableRange) {
        return;
      }

      if (
        editingCell &&
        (editingCell.rowIndex !== rowIndex || editingCell.colIndex !== colIndex)
      ) {
        const activeElement = document.activeElement;
        const editingColumnId = visibleLeafColumns[editingCell.colIndex]?.id;
        const editingColumn = editingColumnId
          ? columnLookup.get(editingColumnId)
          : undefined;
        // Only auto-commit the raw DOM input value for columns without a custom editComponent —
        // for those, the displayed text always equals the value to commit. Custom editors (e.g. an
        // Autocomplete showing a formatted "Name (Id)" label) may display something different from
        // what should actually be persisted, so defer to their own onBlur/onChange commit logic by
        // simply blurring the active element instead of grabbing its raw text ourselves.
        const nextValue = editingColumn?.editComponent
          ? undefined
          : getEditorValueFromBlurTarget(activeElement);

        if (editingColumnId && nextValue !== undefined) {
          applyMultipleEdits([
            {
              rowIndex: editingCell.rowIndex,
              columnId: editingColumnId,
              value: nextValue,
            },
          ]);
        } else if (activeElement instanceof HTMLElement) {
          activeElement.blur();
        }
      }

      const cell = { rowIndex, colIndex };
      if (shiftKey) {
        setRangeEnd(cell);
      } else {
        setSingleCell(cell);
      }
    },
    [
      applyMultipleEdits,
      columnLookup,
      editingCell,
      selectableRange,
      setRangeEnd,
      setSingleCell,
      visibleLeafColumns,
    ]
  );

  const getCellValueByVisibleIndex = useCallback(
    (rowIndex, colIndex) => {
      const row = visibleRows[rowIndex]?.original;
      const columnId = visibleLeafColumns[colIndex]?.id;
      const column = columnLookup.get(columnId);
      if (!row || !column) {
        return undefined;
      }
      return getComputedColumnValue(column, row, rowIndex);
    },
    [columnLookup, visibleLeafColumns, visibleRows]
  );

  const applyFill = useCallback(async () => {
    if (!enableFillHandle || !activeCell || !fillDragEndCell) {
      return;
    }

    const rowStart = Math.min(activeCell.rowIndex, fillDragEndCell.rowIndex);
    const rowEnd = Math.max(activeCell.rowIndex, fillDragEndCell.rowIndex);
    const colStart = Math.min(activeCell.colIndex, fillDragEndCell.colIndex);
    const colEnd = Math.max(activeCell.colIndex, fillDragEndCell.colIndex);
    const sourceValue = getCellValueByVisibleIndex(
      activeCell.rowIndex,
      activeCell.colIndex
    );

    const edits = [];
    for (let rowIndex = rowStart; rowIndex <= rowEnd; rowIndex += 1) {
      for (let colIndex = colStart; colIndex <= colEnd; colIndex += 1) {
        const columnId = visibleLeafColumns[colIndex]?.id;
        if (!columnId) {
          continue;
        }
        edits.push({ rowIndex, columnId, value: sourceValue });
      }
    }
    await applyMultipleEdits(edits);
  }, [
    activeCell,
    applyMultipleEdits,
    enableFillHandle,
    fillDragEndCell,
    getCellValueByVisibleIndex,
    visibleLeafColumns,
  ]);

  useEffect(() => {
    if (!isFillDragging) {
      return;
    }

    const onMouseUp = async () => {
      setIsFillDragging(false);
      await applyFill();
      setFillDragEndCell(null);
    };

    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [applyFill, isFillDragging]);

  useEffect(() => {
    const scrollerElement = scrollerRef.current;
    if (!scrollerElement || typeof ResizeObserver === "undefined") {
      return;
    }

    const updateScrollerMetrics = () => {
      setAvailableWidth(scrollerElement.clientWidth || 0);
      setViewportHeight(scrollerElement.clientHeight || 0);
      setScrollTop(scrollerElement.scrollTop || 0);
    };

    updateScrollerMetrics();

    const resizeObserver = new ResizeObserver(() => {
      updateScrollerMetrics();
    });

    resizeObserver.observe(scrollerElement);
    const onScroll = () => {
      setScrollTop(scrollerElement.scrollTop || 0);
    };
    scrollerElement.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      scrollerElement.removeEventListener("scroll", onScroll);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    if (renderedRows.length === 0) {
      return;
    }

    const scrollerElement = scrollerRef.current;
    if (!scrollerElement) {
      return;
    }

    const firstRenderedRow = scrollerElement.querySelector(
      "tbody tr[data-row-index]"
    );
    if (!(firstRenderedRow instanceof HTMLTableRowElement)) {
      return;
    }

    const measuredHeight = Math.round(
      firstRenderedRow.getBoundingClientRect().height
    );
    if (measuredHeight > 0 && Math.abs(measuredHeight - rowHeight) >= 1) {
      setRowHeight(measuredHeight);
    }
  }, [renderedRows, rowHeight]);

  useEffect(() => {
    const onCopy = (event) => {
      if (
        !enableClipboard ||
        !activeCell ||
        editingCell ||
        isTextInputTarget(event.target)
      ) {
        return;
      }

      const clipboardData = event.clipboardData;
      if (!clipboardData) {
        return;
      }

      const text = buildSelectionClipboardText();
      if (!text) {
        return;
      }

      event.preventDefault();
      clipboardData.setData("text/plain", text);
    };

    const onPaste = async (event) => {
      if (
        !enableClipboard ||
        !activeCell ||
        editingCell ||
        isTextInputTarget(event.target)
      ) {
        return;
      }

      const text = event.clipboardData?.getData("text/plain");
      if (text) {
        event.preventDefault();
        await applyPastedText(text);
        return;
      }

      if (canUseNavigatorClipboard()) {
        event.preventDefault();
        await pasteFromClipboard();
      }
    };

    window.addEventListener("copy", onCopy);
    window.addEventListener("paste", onPaste);

    return () => {
      window.removeEventListener("copy", onCopy);
      window.removeEventListener("paste", onPaste);
    };
  }, [
    activeCell,
    applyPastedText,
    buildSelectionClipboardText,
    editingCell,
    enableClipboard,
    pasteFromClipboard,
  ]);

  useEffect(() => {
    if (!isSelectingRange) {
      return;
    }

    const onMouseUp = () => {
      setIsSelectingRange(false);
    };

    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isSelectingRange]);

  const beginCellEdit = (rowIndex, colIndex) => {
    const columnId = visibleLeafColumns[colIndex]?.id;
    const column = columnLookup.get(columnId);
    if (!column || column.editable === false) {
      return;
    }

    // Optional per-row veto, for columns that only apply to some rows. This is the single entry
    // point for every way of starting an edit (double-click, Enter, type-to-edit), but it is
    // deliberately not consulted by paste or fill — those apply across a range and rely on
    // onCellEdit/onRowEdit returning { cancel: true } to skip individual rows instead.
    const row = visibleRows[rowIndex]?.original;
    if (
      typeof column.isCellEditable === "function" &&
      !column.isCellEditable({ row, rowIndex, column })
    ) {
      return;
    }

    setEditingCell({ rowIndex, colIndex, columnId });
  };

  const commitCellEdit = async (rowIndex, colIndex, value) => {
    const columnId = visibleLeafColumns[colIndex]?.id;
    if (!columnId) {
      return;
    }
    try {
      await applyMultipleEdits([{ rowIndex, columnId, value }]);
    } finally {
      setEditingCell(null);
    }
  };

  const handleGridKeyDown = useCallback(
    (event) => {
      if (editingCell || isTextInputTarget(event.target)) {
        return;
      }

      const rowCount = visibleRows.length;
      const colCount = visibleLeafColumns.length;
      if (rowCount === 0 || colCount === 0) {
        return;
      }

      const currentCell = activeCell ?? { rowIndex: 0, colIndex: 0 };

      if (event.key === "Enter") {
        event.preventDefault();
        beginCellEdit(currentCell.rowIndex, currentCell.colIndex);
        return;
      }

      const deltasByKey = {
        ArrowUp: { rowDelta: -1, colDelta: 0 },
        ArrowDown: { rowDelta: 1, colDelta: 0 },
        ArrowLeft: { rowDelta: 0, colDelta: -1 },
        ArrowRight: { rowDelta: 0, colDelta: 1 },
      };

      const deltas = deltasByKey[event.key];
      if (!deltas) {
        return;
      }

      event.preventDefault();

      const nextRowIndex = Math.min(
        rowCount - 1,
        Math.max(0, currentCell.rowIndex + deltas.rowDelta)
      );
      const nextColIndex = Math.min(
        colCount - 1,
        Math.max(0, currentCell.colIndex + deltas.colDelta)
      );

      setSingleCell({ rowIndex: nextRowIndex, colIndex: nextColIndex });
    },
    [
      activeCell,
      beginCellEdit,
      editingCell,
      setSingleCell,
      visibleLeafColumns.length,
      visibleRows.length,
    ]
  );

  const openColumnControls = useCallback(
    (columnId, anchorEl) => {
      const column = table.getColumn(columnId);
      if (!column) {
        return;
      }
      if (column.columnDef?.meta?.showColumnControls === false) {
        return;
      }
      setColumnControlState({
        open: true,
        anchorEl,
        columnId,
        columnName: column.columnDef.header,
        filterValue: String(column.getFilterValue() ?? ""),
      });
    },
    [table]
  );

  const closeColumnControls = useCallback(() => {
    setColumnControlState((previous) => ({
      ...previous,
      open: false,
      anchorEl: null,
    }));
  }, []);

  const applyColumnFilter = useCallback(
    (value) => {
      const { columnId } = columnControlState;
      if (!columnId) {
        return;
      }

      const column = table.getColumn(columnId);
      if (!column) {
        return;
      }

      column.setFilterValue(value ? value : undefined);
      setColumnControlState((previous) => ({
        ...previous,
        filterValue: value ?? "",
      }));
    },
    [columnControlState, table]
  );

  const setColumnSort = useCallback(
    (direction) => {
      const { columnId } = columnControlState;
      if (!columnId) {
        return;
      }

      if (direction === "none") {
        setSorting((previous) =>
          previous.filter((item) => item.id !== columnId)
        );
      } else {
        setSorting([{ id: columnId, desc: direction === "desc" }]);
      }
    },
    [columnControlState]
  );

  const clearColumnControls = useCallback(() => {
    const { columnId } = columnControlState;
    if (!columnId) {
      return;
    }

    const column = table.getColumn(columnId);
    if (!column) {
      return;
    }

    column.setFilterValue(undefined);
    setSorting((previous) => previous.filter((item) => item.id !== columnId));
    setColumnControlState((previous) => ({ ...previous, filterValue: "" }));
  }, [columnControlState, table]);

  const currentColumnControlSort = useMemo(() => {
    if (!columnControlState.columnId) {
      return "none";
    }
    const sortState = sorting.find(
      (item) => item.id === columnControlState.columnId
    );
    if (!sortState) {
      return "none";
    }
    return sortState.desc ? "desc" : "asc";
  }, [columnControlState.columnId, sorting]);

  const typeAheadOptions = useMemo(() => {
    if (!columnControlState.columnId) {
      return [];
    }
    const column = columnLookup.get(columnControlState.columnId);
    if (!column) {
      return [];
    }

    const cachedEntry = typeAheadCacheRef.current.get(
      columnControlState.columnId
    );
    if (
      cachedEntry &&
      cachedEntry.rowsRef === internalRows &&
      cachedEntry.columnRef === column
    ) {
      return cachedEntry.values;
    }

    const values = new Set();
    internalRows.forEach((row, rowIndex) => {
      const value = getFilterColumnValue(column, row, rowIndex);
      if (value != null && value !== "") {
        values.add(String(value));
      }
    });

    const nextValues = Array.from(values);
    typeAheadCacheRef.current.set(columnControlState.columnId, {
      rowsRef: internalRows,
      columnRef: column,
      values: nextValues,
    });

    return nextValues;
  }, [columnControlState.columnId, columnLookup, internalRows]);

  const borderColor = theme.palette.divider;
  const verticalBorderColor =
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.28)" : borderColor;
  const headerBackground =
    theme.palette.mode === "dark"
      ? theme.palette.secondary.main
      : theme.palette.primary.main;
  const headerForeground = theme.palette.getContrastText(headerBackground);
  const selectedOutline = theme.palette.info.main;
  const rangeFillColor = theme.palette.info.main;
  const fillPreviewColor = theme.palette.info.main;
  const stickySeparatorColor =
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.25)"
      : "rgba(0,0,0,0.22)";
  const rowBackgroundColor =
    theme.palette.mode === "dark"
      ? theme.palette.grey[900]
      : theme.palette.grey[50];
  const alternateRowBackgroundColor =
    theme.palette.mode === "dark"
      ? theme.palette.grey[800]
      : theme.palette.grey[200];
  const cellTextColor =
    theme.palette.mode === "dark"
      ? theme.palette.grey[100]
      : theme.palette.text.primary;
  const headerCheckboxColor =
    theme.palette.mode === "light" ? "#fff" : headerForeground;
  const hasRowClassNameCallback = typeof getRowClassName === "function";
  const hasRowStyleCallback = typeof getRowStyle === "function";
  const hasCellClassNameCallback = typeof getCellClassName === "function";
  const hasCellStyleCallback = typeof getCellStyle === "function";

  return (
    <Paper
      sx={{ p: 0, height, minHeight: 0, width: "100%", flex: 1 }}
      className='tc-data-grid-root'>
      <Box
        className='tc-data-grid-scroller'
        ref={scrollerRef}
        tabIndex={0}
        role='grid'
        aria-label='Data grid'
        onKeyDown={handleGridKeyDown}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}>
          <table className='tc-data-grid-table'>
            <thead className='tc-data-grid-header-row'>
              <SortableContext
                items={columnOrder}
                strategy={horizontalListSortingStrategy}>
                <tr>
                  {table.getHeaderGroups()[0]?.headers.map((header) => {
                    const isSelectColumn =
                      selectColumn && header.column.id === SelectColumnId;
                    const isStickyRight =
                      stickyRightColumnId != null &&
                      header.column.id === stickyRightColumnId;
                    const showColumnControls =
                      !isSelectColumn &&
                      header.column.columnDef?.meta?.showColumnControls !==
                        false;
                    const filterPreviewText = getFilterPreviewText(
                      header.column.getFilterValue()
                    );
                    const headerContent = isSelectColumn ? (
                      <Checkbox
                        size='small'
                        checked={allVisibleRowsSelected}
                        indeterminate={
                          !allVisibleRowsSelected && someVisibleRowsSelected
                        }
                        disabled={visibleSelectedRowIds.length === 0}
                        sx={{
                          color: headerCheckboxColor,
                          "&.Mui-checked": {
                            color: headerCheckboxColor,
                          },
                          "&.MuiCheckbox-indeterminate": {
                            color: headerCheckboxColor,
                          },
                        }}
                        inputProps={{
                          "aria-label": "Select all filtered rows",
                        }}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                        }}
                        onChange={(event) => {
                          toggleVisibleRowsSelection(event.target.checked);
                        }}
                      />
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )
                    );

                    return (
                      <SortableHeaderCell
                        key={header.id}
                        columnId={header.column.id}
                        headerStyles={{
                          width:
                            displayColumnWidths.get(header.column.id) ||
                            header.getSize(),
                          backgroundColor: headerBackground,
                          color: headerForeground,
                          borderRightColor: verticalBorderColor,
                          position: isStickyRight ? "sticky" : undefined,
                          right: isStickyRight ? 0 : undefined,
                          zIndex: isStickyRight ? 6 : undefined,
                          borderLeft: isStickyRight
                            ? `2px solid ${stickySeparatorColor}`
                            : undefined,
                          boxShadow: undefined,
                        }}
                        borderColor={borderColor}
                        onResizeStart={startResize}
                        onAutoSizeColumn={autoSizeColumn}
                        onOpenColumnControls={openColumnControls}
                        isSorted={Boolean(header.column.getIsSorted())}
                        isFiltered={Boolean(header.column.getFilterValue())}
                        filterPreviewText={filterPreviewText}
                        isDraggable={
                          draggableColumns && !isStickyRight && !isSelectColumn
                        }
                        showColumnControls={showColumnControls}
                        showResizer={resizableColumns && !isSelectColumn}>
                        {headerContent}
                      </SortableHeaderCell>
                    );
                  })}
                </tr>
              </SortableContext>
            </thead>
            <tbody>
              {topSpacerHeight > 0 ? (
                <tr aria-hidden='true'>
                  <td
                    style={{ height: topSpacerHeight, padding: 0, border: 0 }}
                    colSpan={visibleLeafColumns.length}
                  />
                </tr>
              ) : null}
              {renderedRows.map((row, virtualRowIndex) => {
                const rowIndex = virtualRange.start + virtualRowIndex;
                let rowContext;
                const getRowContextValue = () => {
                  if (rowContext) {
                    return rowContext;
                  }

                  rowContext = {
                    row: row.original,
                    rowIndex,
                    rowId: row.id,
                    isEvenRow: rowIndex % 2 === 0,
                  };

                  return rowContext;
                };

                const userRowClassName = hasRowClassNameCallback
                  ? getRowClassName(getRowContextValue())
                  : "";
                const userRowStyle = hasRowStyleCallback
                  ? getRowStyle(getRowContextValue())
                  : null;

                return (
                  <tr
                    key={row.id}
                    data-row-index={rowIndex}
                    className={userRowClassName || undefined}
                    style={userRowStyle || undefined}>
                    {row.getVisibleCells().map((cell, colIndex) => {
                      const isActive =
                        activeCell?.rowIndex === rowIndex &&
                        activeCell?.colIndex === colIndex;
                      const inRange = selectableRange
                        ? isCellInRange(rowIndex, colIndex)
                        : false;
                      const inFillPreviewRange = isCellInFillPreviewRange(
                        rowIndex,
                        colIndex
                      );
                      const isEditing =
                        editingCell?.rowIndex === rowIndex &&
                        editingCell?.colIndex === colIndex;
                      const column = columnLookup.get(cell.column.id);
                      const isSelectCell =
                        selectColumn && cell.column.id === SelectColumnId;
                      const cellValue = cell.getValue();
                      const isStickyRight =
                        stickyRightColumnId != null &&
                        cell.column.id === stickyRightColumnId;
                      const baseRowColor =
                        alternateRowColors && rowIndex % 2 === 1
                          ? alternateRowBackgroundColor
                          : rowBackgroundColor;
                      let cellContext;
                      const getCellContextValue = () => {
                        if (cellContext) {
                          return cellContext;
                        }

                        cellContext = {
                          row: row.original,
                          rowIndex,
                          rowId: row.id,
                          column,
                          columnId: cell.column.id,
                          colIndex,
                          value: cellValue,
                          isActive,
                          inRange,
                          inFillPreviewRange,
                          isEditing,
                        };

                        return cellContext;
                      };

                      const userCellClassName = hasCellClassNameCallback
                        ? getCellClassName(getCellContextValue())
                        : "";
                      const userCellStyle = hasCellStyleCallback
                        ? getCellStyle(getCellContextValue())
                        : null;

                      return (
                        <td
                          key={cell.id}
                          className={buildCellClassName({
                            isActive,
                            inRange,
                            inFillPreviewRange,
                            userCellClassName,
                          })}
                          style={{
                            width:
                              displayColumnWidths.get(cell.column.id) ||
                              cell.column.getSize(),
                            borderColor,
                            borderRightColor: verticalBorderColor,
                            outlineColor: selectedOutline,
                            "--tc-grid-range-bg": rangeFillColor,
                            "--tc-grid-fill-preview": fillPreviewColor,
                            backgroundColor: baseRowColor,
                            color: cellTextColor,
                            position: isStickyRight ? "sticky" : undefined,
                            right: isStickyRight ? 0 : undefined,
                            zIndex: isStickyRight ? 2 : undefined,
                            borderLeft: isStickyRight
                              ? `2px solid ${stickySeparatorColor}`
                              : undefined,
                            boxShadow: undefined,
                            backgroundClip: isStickyRight
                              ? "padding-box"
                              : undefined,
                            ...userCellStyle,
                          }}
                          onMouseDown={(event) => {
                            if (isSelectCell) {
                              return;
                            }
                            if (isEditing) {
                              return;
                            }
                            onCellMouseDown(rowIndex, colIndex, event.shiftKey);
                            if (!event.shiftKey) {
                              setIsSelectingRange(true);
                            }
                          }}
                          onMouseEnter={() => {
                            if (isSelectCell) {
                              return;
                            }
                            if (isFillDragging) {
                              setFillDragEndCell({ rowIndex, colIndex });
                              return;
                            }
                            if (isSelectingRange) {
                              setRangeEnd({ rowIndex, colIndex });
                            }
                          }}
                          onDoubleClick={() => {
                            if (!isSelectCell) {
                              beginCellEdit(rowIndex, colIndex);
                            }
                          }}>
                          {isEditing ? (
                            <Box
                              sx={{ width: "100%", minWidth: 0 }}
                              onBlurCapture={(event) => {
                                const nextFocusedElement = event.relatedTarget;
                                if (
                                  nextFocusedElement instanceof Node &&
                                  event.currentTarget.contains(
                                    nextFocusedElement
                                  )
                                ) {
                                  return;
                                }
                                setEditingCell(null);
                              }}>
                              {column.editComponent ? (
                                column.editComponent({
                                  value: cellValue,
                                  row: row.original,
                                  rowIndex,
                                  column,
                                  onCommit: (nextValue) =>
                                    commitCellEdit(
                                      rowIndex,
                                      colIndex,
                                      nextValue
                                    ),
                                  onCancel: () => setEditingCell(null),
                                })
                              ) : (
                                <TextEditorCell
                                  value={cellValue}
                                  onCommit={(nextValue) =>
                                    commitCellEdit(
                                      rowIndex,
                                      colIndex,
                                      nextValue
                                    )
                                  }
                                  onCancel={() => setEditingCell(null)}
                                />
                              )}
                            </Box>
                          ) : (
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          )}
                          {enableFillHandle && isActive ? (
                            <span
                              className='tc-data-grid-fill-handle'
                              style={{ backgroundColor: selectedOutline }}
                              onMouseDown={(event) => {
                                event.preventDefault();
                                setIsFillDragging(true);
                                setFillDragEndCell({ rowIndex, colIndex });
                              }}
                            />
                          ) : null}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {bottomSpacerHeight > 0 ? (
                <tr aria-hidden='true'>
                  <td
                    style={{
                      height: bottomSpacerHeight,
                      padding: 0,
                      border: 0,
                    }}
                    colSpan={visibleLeafColumns.length}
                  />
                </tr>
              ) : null}
            </tbody>
          </table>
        </DndContext>
      </Box>
      <Popover
        open={columnControlState.open}
        anchorEl={columnControlState.anchorEl}
        onClose={closeColumnControls}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { p: 1.5, minWidth: 280, borderRadius: 1.5 } }}>
        <Stack spacing={1.5}>
          <Typography variant='subtitle2'>{`${columnControlState.columnName}`}</Typography>
          <Autocomplete
            freeSolo
            size='small'
            options={typeAheadOptions}
            inputValue={columnControlState.filterValue}
            onInputChange={(_, value) => {
              applyColumnFilter(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`Search ${columnControlState.columnName}...`}
                placeholder={searchPlaceholder}
              />
            )}
          />
          <Stack direction='column' spacing={1}>
            <Button
              size='small'
              variant={
                currentColumnControlSort === "asc" ? "contained" : "outlined"
              }
              onClick={() => setColumnSort("asc")}>
              Sort A-Z
            </Button>
            <Button
              size='small'
              variant={
                currentColumnControlSort === "desc" ? "contained" : "outlined"
              }
              onClick={() => setColumnSort("desc")}>
              Sort Z-A
            </Button>
          </Stack>
          <Stack direction='row' spacing={1} justifyContent='flex-end'>
            <Button size='small' onClick={clearColumnControls}>
              Clear
            </Button>
            <Button size='small' onClick={closeColumnControls}>
              Close
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Paper>
  );
};
